import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, Eye, ArrowRight, Share2, MessageCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import axios from '../api/axios_instance';

const SingleArticle = () => {
  const { id } = useParams();
  const { getLabel, dir } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    axios.get(`api/articles/${id}`)
      .then(res => setArticle(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-2xl font-black text-gray-400">المقال غير موجود</p>
        <Link to="/" className="text-emerald-600 font-bold underline">العودة للرئيسية</Link>
      </div>
    );
  }

  const title = getLabel(article.title);
  const category = getLabel(article.category);
  const content = getLabel(article.content);
  return (
    <article className="min-h-screen bg-white pb-24" dir={dir}>
      <div className="max-w-5xl mx-auto px-4 py-6">

        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-400 mb-10 overflow-hidden whitespace-nowrap">
          <Link to="/" className="hover:text-emerald-600 transition-colors">الرئيسية</Link>
          <ArrowRight className="w-3 h-3" />
          <Link to="/articles" className="hover:text-emerald-600 transition-colors">المقالات</Link>
          <ArrowRight className="w-3 h-3" />
          <span className="text-emerald-600 font-medium truncate">{title}</span>
        </nav>

        {/* Header */}
        <header className="mb-12 text-right" dir="rtl">
          <div className="flex items-center gap-2 mb-4">
            <span className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-bold border border-emerald-100">
              {category}
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black text-gray-900 leading-tight mb-8">
            {title}
          </h1>

          <div className="flex flex-wrap items-center gap-8 text-gray-500 text-sm border-y border-gray-100 py-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600">
                <User size={16} />
              </div>
              <span className="font-medium">{article.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-emerald-600" />
              <span>{article.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-emerald-600" />
              <span>{article.views} قارئ</span>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {article.image && (
          <div className="aspect-[21/9] rounded-[2rem] overflow-hidden mb-16 border border-gray-100">
            <img src={article.image} alt={title} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Content */}
        <div className="max-w-4xl mx-auto">
          <div className="text-right" dir="rtl">
            {content && content.split('\n').map((line, i) => {
              const trimmed = line.trim();
              if (!trimmed) return <div key={i} className="h-4" />;
              // Lines that look like section headings (short, no period, possibly numbered)
              const isHeading = /^[١٢٣٤٥٦٧٨٩\d][\.\-\)]/.test(trimmed) || (trimmed.length < 80 && !trimmed.includes('،') && trimmed.endsWith('\u200c') === false && /^[\u0600-\u06FFa-zA-Z\s\-—]+$/.test(trimmed) && trimmed.length > 5);
              const isBullet = trimmed.startsWith('-');
              if (isBullet) {
                return (
                  <div key={i} className="flex gap-3 items-start mb-2 text-gray-700 text-base leading-relaxed">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                    <span>{trimmed.slice(1).trim()}</span>
                  </div>
                );
              }
              if (isHeading && trimmed.length < 80) {
                return (
                  <h2 key={i} className="text-xl font-black text-gray-900 mt-10 mb-4 border-r-4 border-emerald-500 pr-3">
                    {trimmed}
                  </h2>
                );
              }
              return (
                <p key={i} className="text-base md:text-lg text-gray-700 leading-relaxed mb-4">
                  {trimmed}
                </p>
              );
            })}

            {/* Footer */}
            <footer className="pt-12 mt-12 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-gray-900">انشر المعرفة:</span>
                <div className="flex gap-2">
                  <button className="w-12 h-12 flex items-center justify-center bg-[#1877F2]/10 text-[#1877F2] rounded-2xl hover:bg-[#1877F2] hover:text-white transition-all font-black text-xs">
                    f
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center bg-[#25D366]/10 text-[#25D366] rounded-2xl hover:bg-[#25D366] hover:text-white transition-all">
                    <MessageCircle size={24} />
                  </button>
                  <button className="w-12 h-12 flex items-center justify-center bg-gray-100 text-gray-600 rounded-2xl hover:bg-emerald-600 hover:text-white transition-all">
                    <Share2 size={24} />
                  </button>
                </div>
              </div>
              <div className="text-gray-400 text-sm">{article.date} | مشروع بن موسى</div>
            </footer>
          </div>
        </div>

      </div>
    </article>
  );
};

export default SingleArticle;
